import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { ymd } from "./journal/utils";
import JournalStats from "./journal/JournalStats";
import JournalInsights from "./journal/JournalInsights";
import JournalPnLChart from "./journal/JournalPnLChart";
import JournalCalendar from "./journal/JournalCalendar";
import JournalDayForm from "./journal/JournalDayForm";

/**
 * TradeJournal — orchestrator.
 * Loads journal entries for a month, derives per-day aggregates + monthly totals,
 * and delegates rendering to focused subcomponents in `./journal/`.
 */
export default function TradeJournal() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed
  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(ymd(today));

  const monthKey = useMemo(() => `${year}-${String(month + 1).padStart(2, "0")}`, [year, month]);

  const load = async () => {
    try {
      const { data } = await api.get(`/journal?month=${monthKey}`);
      setEntries(data);
    } catch (err) { logDev(err); }
  };

  useEffect(() => { load(); }, [monthKey]);

  // Aggregate per day
  const byDate = useMemo(() => {
    const map = {};
    for (const e of entries) {
      if (!map[e.date]) map[e.date] = { trade: 0, deposit: 0, withdraw: 0, entries: [] };
      if (e.kind === "trade") map[e.date].trade += e.amount;
      else if (e.kind === "deposit") map[e.date].deposit += e.amount;
      else if (e.kind === "withdraw") map[e.date].withdraw += e.amount;
      map[e.date].entries.push(e);
    }
    return map;
  }, [entries]);

  // Monthly totals
  const totals = useMemo(() => {
    let profit = 0, loss = 0, deposit = 0, withdraw = 0, wins = 0, losses = 0;
    for (const e of entries) {
      if (e.kind === "trade") {
        if (e.amount >= 0) { profit += e.amount; wins += 1; }
        else { loss += Math.abs(e.amount); losses += 1; }
      } else if (e.kind === "deposit") deposit += e.amount;
      else if (e.kind === "withdraw") withdraw += e.amount;
    }
    return { profit, loss, netPnl: profit - loss, deposit, withdraw, wins, losses };
  }, [entries]);

  const gotoMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y);
  };

  const exportCsv = () => {
    if (entries.length === 0) { toast.warning("No entries to export."); return; }
    const rows = [["Date", "Kind", "Amount", "Note"]];
    // Sort chronologically for a nice CSV.
    const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
    for (const e of sorted) {
      const note = (e.note || "").replace(/"/g, '""');
      rows.push([e.date, e.kind, Number(e.amount).toFixed(2), `"${note}"`]);
    }
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trade-journal-${monthKey}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  const selected = byDate[selectedDate];

  return (
    <div data-testid="trade-journal">
      <JournalStats totals={totals} />
      <JournalInsights totals={totals} />
      <div className="flex justify-end -mt-2 mb-4">
        <button
          data-testid="journal-export-csv"
          onClick={exportCsv}
          disabled={entries.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-[#00b4ff] hover:text-[#00b4ff] disabled:opacity-50 text-xs font-mono-plex font-semibold tracking-widest uppercase"
          style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
        >
          <Download size={12} /> Export CSV
        </button>
      </div>
      <JournalPnLChart year={year} month={month} entries={entries} />
      <JournalCalendar
        year={year}
        month={month}
        byDate={byDate}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        onGotoMonth={gotoMonth}
      />
      <JournalDayForm
        selectedDate={selectedDate}
        selected={selected}
        onChanged={load}
      />
    </div>
  );
}
